import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EFreightForCarrierStatus,
  EUserRole,
  EVehicleType,
  IFreightBasicForCarrier,
  IUser,
  IVehicle,
} from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { AccountService } from '../../account';
import { UserEditModalComponent } from '../../user/user-edit-modal/user-edit-modal.component';
import { UsersStoreService } from '../../user/users-store.service';
import { VehicleEditModalComponent } from '../../vehicle/vehicle-edit-modal/vehicle-edit-modal.component';
import { VehiclesStoreService } from '../../vehicle/vehicles-store.service';
import { FreightCarrierInprogressCardBasicComponent } from '../freight-carrier-inprogress-card-basic/freight-carrier-inprogress-card-basic.component';
import { FreightCarrierMutationsService } from '../freight-carrier-mutations.service';
import {
  IAssignationDriverOption,
  IAssignationVehicleOption,
  NEW_ASSIGNATION_DRIVER_ID,
  NEW_ASSIGNATION_VEHICLE_ID,
  NO_ASSIGNATION_SELECTION,
} from '../freight-carrier-store.model';
import { FreightCarrierStoreService } from '../freight-carrier-store.service';

@Component({
  selector: 'lib-freight-carrier-assignation-modal',
  templateUrl: './freight-carrier-assignation-modal.component.html',
  styleUrls: ['./freight-carrier-assignation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonContent,
    IonTitle,
    IonIcon,
    IonButtons,
    IonHeader,
    IonButton,
    IonRow,
    IonGrid,
    IonToolbar,
    IonCol,
    IonFooter,
    IonSelect,
    IonSelectOption,
    FreightCarrierInprogressCardBasicComponent,
    TranslateModule,
  ],
})
export class FreightCarrierAssignationModalComponent implements OnInit {
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly accountService = inject(AccountService);
  private readonly usersStoreService = inject(UsersStoreService);
  private readonly vehiclesStoreService = inject(VehiclesStoreService);
  private readonly freightCarrierStoreService = inject(FreightCarrierStoreService);
  private readonly freightCarrierMutationsService = inject(FreightCarrierMutationsService);

  // comes from the component that opens the modal (ModalController componentProps).
  public freight!: IFreightBasicForCarrier;

  // The users and vehicles queries are not refreshed by the general application refresh: this
  // component reloads both when it is initialized (before the prefill effect runs).
  public ngOnInit(): void {
    this.usersStoreService.reloadUsersOnComponentInit();
    this.vehiclesStoreService.reloadVehiclesOnComponentInit();
  }

  protected readonly noSelection = NO_ASSIGNATION_SELECTION;

  /**
   * Full assignment (driver + vehicles) requires CRUD_VEHICLES. Drivers (INTERACT_WITH_STOPS) can
   * only assign vehicles; CRUD_VEHICLES prevails when both are present.
   */
  protected readonly canAssignDriver = computed(() => this.accountService.hasCapability(ECapabilities.CRUD_VEHICLES));
  private readonly canCreateDriver = computed(() =>
    this.usersStoreService.manageableRoles().includes(EUserRole.DRIVER),
  );
  private readonly canCreateVehicle = computed(() => this.accountService.hasCapability(ECapabilities.CRUD_VEHICLES));

  /** The driver can only be changed with CRUD_VEHICLES and before the freight starts. */
  protected readonly canChangeDriver = computed(
    () => this.canAssignDriver() && this.freight.status === EFreightForCarrierStatus.ASSIGNED,
  );

  protected readonly drivers = computed<IAssignationDriverOption[]>(() => {
    const options = this.usersStoreService
      .usersVM()
      .items.filter((user) => user.enabled && user.roles.includes(EUserRole.DRIVER))
      .map((user) => ({ id: user.id, label: `${user.firstName} ${user.lastName}`.trim() }));

    return this.canCreateDriver()
      ? [
          ...options,
          { id: NEW_ASSIGNATION_DRIVER_ID, label: this.translate.instant('freight.new_driver'), isNew: true },
        ]
      : options;
  });

  // TRACTOR vehicles are offered as motorized vehicles (cabezas tractoras).
  protected readonly tractors = computed<IAssignationVehicleOption[]>(() => {
    const options = this.vehiclesStoreService
      .vehiclesVM()
      .items.filter((vehicle) => vehicle.type === EVehicleType.TRACTOR)
      .map((vehicle) => ({ id: vehicle.id, label: vehicle.plateNumber }));

    return this.canCreateVehicle() ? [...options, this.newVehicleOption()] : options;
  });

  // CANVAS and BOX vehicles are offered as trailers (remolques).
  protected readonly trailers = computed<IAssignationVehicleOption[]>(() => {
    const options = this.vehiclesStoreService
      .vehiclesVM()
      .items.filter((vehicle) => vehicle.type === EVehicleType.CANVAS || vehicle.type === EVehicleType.BOX)
      .map((vehicle) => ({ id: vehicle.id, label: vehicle.plateNumber }));

    return this.canCreateVehicle() ? [...options, this.newVehicleOption()] : options;
  });

  protected assignationForm: FormGroup = this.fb.group({
    driver: [NO_ASSIGNATION_SELECTION],
    tractor: [NO_ASSIGNATION_SELECTION],
    trailer: [NO_ASSIGNATION_SELECTION],
  });

  private prefilled = false;
  private lastDriverValue: unknown = NO_ASSIGNATION_SELECTION;
  private lastTractorValue: unknown = NO_ASSIGNATION_SELECTION;
  private lastTrailerValue: unknown = NO_ASSIGNATION_SELECTION;

  constructor() {
    // The stores load asynchronously: prefill the form once the options are available.
    effect(() => {
      const drivers = this.drivers();
      const tractors = this.tractors();
      const trailers = this.trailers();
      const isLoading = this.usersStoreService.usersVM().isLoading || this.vehiclesStoreService.vehiclesVM().isLoading;

      if (this.prefilled || isLoading || !this.freight) return;

      this.prefilled = true;
      this.prefill(drivers, tractors, trailers);
    });
  }

  protected cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  protected onDriverChange(value: unknown): void {
    if ((value as IAssignationDriverOption | null)?.isNew) {
      void this.createDriver();
      return;
    }
    this.lastDriverValue = value;
  }

  protected onTractorChange(value: unknown): void {
    if ((value as IAssignationVehicleOption | null)?.isNew) {
      void this.createVehicle('tractor');
      return;
    }
    this.lastTractorValue = value;
  }

  protected onTrailerChange(value: unknown): void {
    if ((value as IAssignationVehicleOption | null)?.isNew) {
      void this.createVehicle('trailer');
      return;
    }
    this.lastTrailerValue = value;
  }

  protected async confirm(): Promise<boolean> {
    const formValues = this.assignationForm.getRawValue();
    const driver = this.toDriverOption(formValues.driver);
    const tractor = this.toVehicleOption(formValues.tractor);
    const trailer = this.toVehicleOption(formValues.trailer);

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    loading.present();

    try {
      await this.freightCarrierMutationsService.assignVehiclesAndDriver(
        this.freight,
        driver,
        tractor,
        trailer,
        this.canChangeDriver(),
      );

      loading.message = this.translate.instant('general.loading_data');
      await this.freightCarrierStoreService.reloadAfterSaveData();

      return this.modalCtrl.dismiss(null, 'confirm');
    } catch (error) {
      console.error('Error al asignar vehículos y conductor:', error);
      return false;
    } finally {
      await loading.dismiss();
    }
  }

  /** An unselected option is sent as explicit `null` (to unassign). */
  private toDriverOption(value: unknown): IAssignationDriverOption | null {
    return value === NO_ASSIGNATION_SELECTION ? null : (value as IAssignationDriverOption);
  }

  private toVehicleOption(value: unknown): IAssignationVehicleOption | null {
    return value === NO_ASSIGNATION_SELECTION ? null : (value as IAssignationVehicleOption);
  }

  private newVehicleOption(): IAssignationVehicleOption {
    return { id: NEW_ASSIGNATION_VEHICLE_ID, label: this.translate.instant('freight.new_vehicle'), isNew: true };
  }

  private prefill(
    drivers: IAssignationDriverOption[],
    tractors: IAssignationVehicleOption[],
    trailers: IAssignationVehicleOption[],
  ): void {
    // Disable the driver control (instead of the `disabled` attribute) to avoid the Angular warning.
    if (!this.canChangeDriver()) {
      this.assignationForm.controls['driver'].disable({ emitEvent: false });
    }

    if (this.canAssignDriver()) {
      const currentDriver = this.freight.drivers?.[0];
      this.lastDriverValue = currentDriver
        ? (drivers.find((option) => option.id === currentDriver.id) ?? NO_ASSIGNATION_SELECTION)
        : NO_ASSIGNATION_SELECTION;
      this.assignationForm.controls['driver'].setValue(this.lastDriverValue);
    }

    const currentTractor = this.findAssignedVehicle(tractors);
    this.lastTractorValue = currentTractor
      ? (tractors.find((option) => option.id === currentTractor.id) ?? NO_ASSIGNATION_SELECTION)
      : NO_ASSIGNATION_SELECTION;
    this.assignationForm.controls['tractor'].setValue(this.lastTractorValue);

    const currentTrailer = this.findAssignedVehicle(trailers);
    this.lastTrailerValue = currentTrailer
      ? (trailers.find((option) => option.id === currentTrailer.id) ?? NO_ASSIGNATION_SELECTION)
      : NO_ASSIGNATION_SELECTION;
    this.assignationForm.controls['trailer'].setValue(this.lastTrailerValue);
  }

  private findAssignedVehicle(options: IAssignationVehicleOption[]): { id: string } | undefined {
    return this.freight.vehicles?.find((vehicle) => options.some((option) => option.id === vehicle.id));
  }

  private async createDriver(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: UserEditModalComponent,
      componentProps: { user: null, presetRoles: [EUserRole.DRIVER] },
      backdropDismiss: false,
    });
    await modal.present();

    const { data, role } = await modal.onDidDismiss<IUser>();

    if (role !== 'confirm' || !data) {
      this.assignationForm.controls['driver'].setValue(this.lastDriverValue);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 300));
    const option = this.drivers().find((driver) => driver.id === data.id);
    this.lastDriverValue = option ?? NO_ASSIGNATION_SELECTION;
    this.assignationForm.controls['driver'].setValue(this.lastDriverValue);
  }

  private async createVehicle(requested: 'tractor' | 'trailer'): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: VehicleEditModalComponent,
      componentProps: { vehicle: null },
      backdropDismiss: false,
    });
    await modal.present();

    const { data, role } = await modal.onDidDismiss<IVehicle>();

    if (role !== 'confirm' || !data) {
      this.assignationForm.controls[requested].setValue(
        requested === 'tractor' ? this.lastTractorValue : this.lastTrailerValue,
      );
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 300));

    // Matches the selectors: only TRACTOR is a tractor; BOX is offered as a trailer.
    const isTractor = data.type === EVehicleType.TRACTOR;
    const target: 'tractor' | 'trailer' = isTractor ? 'tractor' : 'trailer';
    const options = isTractor ? this.tractors() : this.trailers();
    const option = options.find((item) => item.id === data.id) ?? NO_ASSIGNATION_SELECTION;

    if (target === 'tractor') {
      this.lastTractorValue = option;
    } else {
      this.lastTrailerValue = option;
    }
    this.assignationForm.controls[target].setValue(option);

    if (target !== requested) {
      const previous = requested === 'tractor' ? this.lastTractorValue : this.lastTrailerValue;
      this.assignationForm.controls[requested].setValue(previous);
    }
  }
}
